// Thin per-app wrapper around the shared @lf/analytics-events package
// for the student app (`test-yted`). The student app is COPPA-scoped:
// consent is denied by default and only flips to granted when the
// child profile carries `data.collect_usage_data === true`.
//
// Per design §D *Provider abstraction*, no call site outside this
// file may import gtag/react-ga4 directly. The vendor adapter lives
// in @lf/analytics-events.

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
    init,
    pageView,
    routeIdForPath,
    setConsent,
    identify,
    identifyByProfile,
    track,
    setAppRole,
} from '@lf/analytics-events';

const APP_ROLE = 'child';

export function analyticsProvider(env = process.env) {
    if (env.REACT_APP_ANALYTICS_PROVIDER) {
        return env.REACT_APP_ANALYTICS_PROVIDER;
    }
    // Production should fail toward real collection, while local/test builds
    // remain on the deterministic in-memory adapter by default.
    return env.NODE_ENV === 'production' ? 'ga4' : 'memory';
}

export function analyticsConfig(env = process.env) {
    return {
        provider: analyticsProvider(env),
        measurementId: env.REACT_APP_GA4_ID || 'G-QBTFWYNDL1',
        environment: env.REACT_APP_LF_ENV
            || (env.NODE_ENV === 'production' ? 'prod' : 'local'),
        appRole: APP_ROLE,
    };
}

export function initializeAnalytics() {
    init({
        ...analyticsConfig(),
        // debug_mode routes events to DebugView and excludes them from
        // standard / Realtime reports. Tie it to an explicit env var
        // rather than NODE_ENV.
        debug: process.env.REACT_APP_GA_DEBUG === '1'
            || (typeof window !== 'undefined'
                && window.location && window.location.search
                && /[?&]ga_debug=1/.test(window.location.search)),
    });
    // Default-denied consent for student app per COPPA. Flipped to
    // granted by setUpAnalytics() once a profile with collect_usage_data
    // is loaded.
    setConsent('denied');
}

export function usePageTracking() {
    const location = useLocation();
    useEffect(() => {
        pageView(routeIdForPath(location.pathname, { appRole: APP_ROLE }), {});
    }, [location]);
}

export async function setUpAnalytics(profile) {
    if (profile && profile.data && profile.data.collect_usage_data) {
        setConsent('granted');
        await identifyByProfile({
            role: 'child',
            profileId: profile.user_profile_id || profile.username,
            salt: process.env.REACT_APP_LF_ANALYTICS_SALT || '',
            userProperties: {
                grade_level: profile.grade_level,
            },
        });
    } else {
        setNoDataCollection();
    }
}

export function setNoDataCollection() {
    setConsent('denied');
    identify(null, {});
}

export { track, setConsent, setAppRole };
